import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { fetchActiveAnnouncements, dismissAnnouncement } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Megaphone, X, Volume2, Pause, Play, Square } from "lucide-react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

/**
 * Renders the newest active announcement the current user hasn't dismissed.
 * Mounted globally by App.js — silently returns null when there's nothing
 * to show, the user isn't authenticated, or the user is inside the admin
 * console (its DialogOverlay would block every admin control otherwise).
 *
 * Includes an opt-in "Listen" narration built on the Web Speech API — clicks,
 * not autoplay, so it never fights a user's own screen reader or a browser
 * autoplay policy. Chunked by sentence so long bodies don't get cut off in
 * Chrome. Stops on unmount / dismiss.
 */

const MAX_CHUNK_CHARS = 240;

function chunk(text) {
  const clean = (text || "").replace(/\s+/g, " ").trim();
  if (!clean) return [];
  const sentences = clean.match(/[^.!?]+[.!?]+["')\]]*|\s*[^.!?]+$/g) || [clean];
  const out = [];
  let buf = "";
  for (const s of sentences) {
    const p = s.trim();
    if (!p) continue;
    if ((buf + " " + p).trim().length > MAX_CHUNK_CHARS && buf) {
      out.push(buf.trim());
      buf = p;
    } else {
      buf = (buf + " " + p).trim();
    }
  }
  if (buf) out.push(buf.trim());
  return out;
}

export default function AnnouncementModal() {
  const { user } = useAuth();
  const location = useLocation();
  const onAdmin = location.pathname.startsWith("/admin");
  const onPricing = location.pathname.startsWith("/pricing");
  const onPartner = location.pathname.startsWith("/partner");
  const onLibrary = location.pathname.startsWith("/library");
  const [onboardingBlocking, setOnboardingBlocking] = useState(false);
  const suppressed = onAdmin || onPricing || onPartner || onLibrary || onboardingBlocking;
  const [current, setCurrent] = useState(null);
  const [dismissing, setDismissing] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const supportsSpeech =
    typeof window !== "undefined" && "speechSynthesis" in window;
  const cancelRef = useRef(false);

  // Check onboarding status — if the user hasn't finished age/terms, DO NOT
  // render the announcement (its overlay would block the onboarding CTAs).
  useEffect(() => {
    if (!user) {
      setOnboardingBlocking(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        const r = await fetch(`${API}/terms/status`, { credentials: "include" });
        if (!r.ok) {
          if (!cancelled) setOnboardingBlocking(false);
          return;
        }
        const s = await r.json();
        if (!cancelled) {
          setOnboardingBlocking(
            Boolean(
              s.needs_age_verification || s.needs_parent_consent || s.needs_terms || s.needs_privacy
            )
          );
        }
      } catch {
        if (!cancelled) setOnboardingBlocking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user || suppressed) return;
    (async () => {
      try {
        const r = await fetchActiveAnnouncements();
        if (r?.announcements?.length) {
          setCurrent(r.announcements[0]);
        }
      } catch (e) {
        console.error("announcement fetch failed", e);
      }
    })();
  }, [user, suppressed]);

  // Stop any narration when the modal unmounts or the current announcement changes.
  useEffect(() => {
    return () => {
      if (supportsSpeech) window.speechSynthesis.cancel();
    };
  }, [supportsSpeech, current?.id]);

  if (!current || suppressed) return null;

  const stopSpeech = () => {
    cancelRef.current = true;
    if (supportsSpeech) window.speechSynthesis.cancel();
    setSpeaking(false);
    setPaused(false);
  };

  const startSpeech = () => {
    if (!supportsSpeech) return;
    stopSpeech();
    cancelRef.current = false;
    const script = `${current.title}. ${current.body}`;
    const parts = chunk(script);
    let i = 0;
    const speakNext = () => {
      if (cancelRef.current || i >= parts.length) {
        setSpeaking(false);
        setPaused(false);
        return;
      }
      const u = new SpeechSynthesisUtterance(parts[i]);
      u.rate = 0.95;
      u.pitch = 1;
      u.volume = 1;
      u.lang = document.documentElement.lang || "en-US";
      u.onend = () => {
        i += 1;
        speakNext();
      };
      u.onerror = () => {
        setSpeaking(false);
        setPaused(false);
      };
      window.speechSynthesis.speak(u);
    };
    setSpeaking(true);
    setPaused(false);
    speakNext();
  };

  const pauseSpeech = () => {
    if (!supportsSpeech) return;
    if (paused) {
      window.speechSynthesis.resume();
      setPaused(false);
    } else {
      window.speechSynthesis.pause();
      setPaused(true);
    }
  };

  const close = async () => {
    if (!current) return;
    stopSpeech();
    setDismissing(true);
    try {
      await dismissAnnouncement(current.id);
    } catch (e) {
      console.error("dismiss failed", e);
    } finally {
      setCurrent(null);
      setDismissing(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v && current?.dismissible !== false) close();
      }}
    >
      <DialogContent
        data-testid="announcement-modal"
        className="max-w-lg bg-obsidian-800 border-obsidian-600 rounded-none p-0"
      >
        <VisuallyHidden asChild>
          <DialogTitle>{current.title}</DialogTitle>
        </VisuallyHidden>
        <VisuallyHidden asChild>
          <DialogDescription>Umbra announcement</DialogDescription>
        </VisuallyHidden>
        <div className="p-8 md:p-10">
          <div className="flex items-center justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <Megaphone className="h-4 w-4 text-terracotta" aria-hidden="true" />
              <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta">
                A note from Umbra
              </p>
            </div>
            {supportsSpeech && (
              !speaking ? (
                <button
                  onClick={startSpeech}
                  data-testid="announcement-listen-btn"
                  aria-label="Listen to this note"
                  title="Listen to this note"
                  className="inline-flex items-center gap-2 px-3 py-1.5 font-sans text-[0.65rem] uppercase tracking-[0.2em] text-terracotta border border-terracotta/60 hover:bg-terracotta hover:text-obsidian-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta transition-colors"
                >
                  <Volume2 className="h-3 w-3" aria-hidden="true" /> Listen
                </button>
              ) : (
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={pauseSpeech}
                    data-testid="announcement-pause-btn"
                    aria-label={paused ? "Resume narration" : "Pause narration"}
                    title={paused ? "Resume" : "Pause"}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 font-sans text-[0.65rem] uppercase tracking-[0.2em] text-terracotta border border-terracotta/60 hover:bg-terracotta hover:text-obsidian-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta transition-colors"
                  >
                    {paused ? <Play className="h-3 w-3" aria-hidden="true" /> : <Pause className="h-3 w-3" aria-hidden="true" />}
                    {paused ? "Resume" : "Pause"}
                  </button>
                  <button
                    onClick={stopSpeech}
                    data-testid="announcement-stop-btn"
                    aria-label="Stop narration"
                    title="Stop"
                    className="inline-flex items-center px-2.5 py-1.5 text-terracotta border border-terracotta/60 hover:bg-terracotta hover:text-obsidian-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta transition-colors"
                  >
                    <Square className="h-3 w-3" aria-hidden="true" />
                  </button>
                </div>
              )
            )}
          </div>

          <h2
            className="font-serif text-2xl md:text-3xl text-bone-100 leading-snug mb-4"
            data-testid="announcement-title"
          >
            {current.title}
          </h2>
          <p
            className="font-serif italic text-bone-300 leading-relaxed whitespace-pre-line mb-8"
            data-testid="announcement-body"
          >
            {current.body}
          </p>

          <div className="flex items-center justify-between gap-4 pt-4 border-t border-obsidian-600">
            {current.cta_url && current.cta_label ? (
              <a
                href={current.cta_url}
                target={current.cta_url.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                onClick={close}
                data-testid="announcement-cta"
                className="inline-block bg-terracotta hover:bg-terracotta-deep text-obsidian-900 px-5 py-2.5 font-sans text-xs uppercase tracking-[0.2em] font-semibold transition-colors"
              >
                {current.cta_label}
              </a>
            ) : (
              <span />
            )}
            {current.dismissible !== false && (
              <button
                onClick={close}
                disabled={dismissing}
                data-testid="announcement-dismiss-btn"
                className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-bone-400 hover:text-bone-100 transition-colors"
              >
                <X className="h-3 w-3" aria-hidden="true" /> Got it
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
